/**
 * Renders only the rows of a long table that are near the viewport. The list
 * screens are not bounded by the viewport (the page grows with the list), so
 * the window scroll position is what drives the window of rendered rows; two
 * spacer rows keep the table height and the scrollbar.
 *
 * Row heights are assumed uniform: the first rendered row is measured once and
 * the fallback is used until then.
 */
import { useLayoutEffect, useState, type RefObject } from 'react';

export interface VirtualRows {
  /** Index of the first rendered row (inclusive) and the last one (exclusive). */
  start: number;
  end: number;
  /** Heights of the spacer rows before and after the rendered ones. */
  topSpacer: number;
  bottomSpacer: number;
  rowHeight: number;
}

export const OVERSCAN_ROWS = 10;

export const visibleRange = (
  scrollTop: number,
  viewportHeight: number,
  rowCount: number,
  rowHeight: number,
  overscan = OVERSCAN_ROWS,
): { start: number; end: number } => {
  // An even start keeps the zebra striping (`nth-child`) aligned once the spacer row is inserted.
  const first = Math.floor(scrollTop / rowHeight) - overscan;
  const evenFirst = first - (((first % 2) + 2) % 2);
  const last = Math.ceil((scrollTop + viewportHeight) / rowHeight) + overscan;
  return {
    start: Math.max(0, Math.min(evenFirst, rowCount)),
    end: Math.max(0, Math.min(last, rowCount)),
  };
};

export const useWindowVirtualRows = (
  /** The `<tbody>` (or any ancestor whose top is the first row's top). */
  bodyRef: RefObject<HTMLElement | null>,
  rowCount: number,
  rowSelector: string,
  fallbackRowHeight = 54,
): VirtualRows => {
  const [rowHeight, setRowHeight] = useState(fallbackRowHeight);
  const [range, setRange] = useState({ start: 0, end: 0 });

  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const top = body.getBoundingClientRect().top + window.scrollY;
      const scrollTop = Math.max(0, window.scrollY - top);
      const next = visibleRange(
        scrollTop,
        window.innerHeight,
        rowCount,
        rowHeight,
      );
      setRange((current) =>
        current.start === next.start && current.end === next.end
          ? current
          : next,
      );
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [bodyRef, rowCount, rowHeight]);

  // Measure a real row once some are rendered (jsdom reports 0: keep the fallback).
  useLayoutEffect(() => {
    const row = bodyRef.current?.querySelector<HTMLElement>(rowSelector);
    const height = row?.offsetHeight ?? 0;
    if (height > 0 && height !== rowHeight) setRowHeight(height);
  }, [bodyRef, rowSelector, rowHeight, range.end]);

  return {
    start: range.start,
    end: range.end,
    topSpacer: range.start * rowHeight,
    bottomSpacer: Math.max(0, rowCount - range.end) * rowHeight,
    rowHeight,
  };
};
